export type Lang = "en" | "ar";

type Dict = Record<string, { en: string; ar: string }>;

const dict: Dict = {
  // ── Accessibility ──────────────────────────────────────
  "a11y.skip": { en: "Skip to content", ar: "تخطي إلى المحتوى" },

  // ── Navigation ─────────────────────────────────────────
  "nav.home": { en: "Home", ar: "الرئيسية" },
  "nav.about": { en: "About", ar: "من نحن" },
  "nav.services": { en: "Services", ar: "الخدمات" },
  "nav.products": { en: "Products", ar: "المنتجات" },
  "nav.blog": { en: "Blog", ar: "المدونة" },
  "nav.gallery": { en: "Gallery", ar: "المعرض" },
  "nav.contact": { en: "Contact", ar: "تواصل معنا" },
  "nav.search": { en: "Search", ar: "بحث" },
  "nav.clients": { en: "Clients", ar: "العملاء" },
  "preview.banner": {
    en: "Preview mode — this page is not published. Only you can see this link.",
    ar: "وضع المعاينة — هذه الصفحة غير منشورة. أنت فقط من يمكنه رؤية هذا الرابط.",
  },
  "maintenance.preview.banner": {
    en: "Maintenance mode is ON for visitors — you are previewing the live site as an admin. Refresh after saving CMS changes.",
    ar: "وضع الصيانة مفعّل للزوار — أنت تعاين الموقع الحي كمسؤول. حدّث الصفحة بعد حفظ تغييرات المحتوى.",
  },
  "nav.aria": { en: "Primary navigation", ar: "التنقل الرئيسي" },

  // ── Search ─────────────────────────────────────────────
  "search.title": { en: "Search", ar: "بحث" },
  "search.placeholder": { en: "Search the site…", ar: "ابحث في الموقع…" },
  "search.submit": { en: "Search", ar: "بحث" },
  "search.hint": {
    en: "Enter at least 2 characters.",
    ar: "أدخل حرفين على الأقل.",
  },
  "search.noQuery": {
    en: "Type a keyword to find pages, blog posts, products, and services.",
    ar: "اكتب كلمة مفتاحية للعثور على الصفحات والمدونة والمنتجات والخدمات.",
  },
  "search.none": {
    en: "No results found. Try different keywords.",
    ar: "لا توجد نتائج. جرّب كلمات مفتاحية أخرى.",
  },
  "search.resultsFor": { en: "Results for", ar: "نتائج البحث عن" },
  "search.type.blog": { en: "Blog", ar: "مدونة" },
  "search.type.product": { en: "Product", ar: "منتج" },
  "search.type.service": { en: "Service", ar: "خدمة" },
  "search.type.page": { en: "Page", ar: "صفحة" },

  // ── Language switcher ──────────────────────────────────
  "lang.switch.to.ar": { en: "العربية", ar: "English" },
  "lang.switch.aria": {
    en: "Switch to Arabic",
    ar: "Switch to English",
  },

  // ── Footer ─────────────────────────────────────────────
  "footer.quickLinks": { en: "Quick Links", ar: "روابط سريعة" },
  "footer.legal": { en: "Legal", ar: "قانوني" },
  "footer.contact": { en: "Contact", ar: "اتصل بنا" },
  "footer.rights": { en: "All rights reserved.", ar: "جميع الحقوق محفوظة." },

  // ── About page ─────────────────────────────────────────
  "about.badge": { en: "Who we are", ar: "من نحن" },
  "about.title": { en: "About D&A Systems", ar: "عن D&A Systems" },
  "about.subtitle": {
    en: "Technology advisory and delivery focused on resilience, innovation, and measurable outcomes.",
    ar: "استشارات وتنفيذ تقني متخصص في الصمود والابتكار ونتائج قابلة للقياس.",
  },
  "about.mission": { en: "Our mission", ar: "مهمتنا" },
  "about.mission.p1": {
    en: "At D&A Systems, our mission is to empower global enterprises by integrating cutting-edge technologies, resilient digital infrastructures, and strategic governance—so technology becomes a seamless enabler of business success.",
    ar: "مهمتنا في D&A Systems هي تمكين المؤسسات العالمية من خلال دمج أحدث التقنيات والبنى التحتية الرقمية المتينة والحوكمة الاستراتيجية، لتصبح التكنولوجيا محركاً سلساً لنجاح الأعمال.",
  },
  "about.mission.p2": {
    en: "We partner with organizations of all sizes—from growing SMEs to global enterprises—to design, deploy, and manage IT solutions that deliver measurable value and long-term resilience.",
    ar: "نتعاون مع مؤسسات من جميع الأحجام—من الشركات الصغيرة والمتوسطة المتنامية إلى المؤسسات العالمية—لتصميم ونشر وإدارة حلول تقنية تحقق قيمة ملموسة وصموداً طويل الأمد.",
  },
  "about.values": { en: "Core values", ar: "قيمنا الأساسية" },
  "about.values.resilience.title": {
    en: "Resilience & trust",
    ar: "الصمود والثقة",
  },
  "about.values.resilience.desc": {
    en: "Security and resilience are foundational—we protect operations against an evolving threat landscape.",
    ar: "الأمن والصمود ركيزة أساسية—نحمي عمليات عملائنا في مواجهة المشهد المتطور للتهديدات.",
  },
  "about.values.innovation.title": {
    en: "Innovation driven",
    ar: "مدفوعون بالابتكار",
  },
  "about.values.innovation.desc": {
    en: "We invest in AI and ML research so clients benefit from advanced, practical technology.",
    ar: "نستثمر في أبحاث الذكاء الاصطناعي والتعلم الآلي لتزويد عملائنا بتقنيات متقدمة وعملية.",
  },
  "about.values.client.title": {
    en: "Client focused",
    ar: "محور العميل",
  },
  "about.values.client.desc": {
    en: "Every engagement is tailored to your industry, constraints, and growth objectives.",
    ar: "كل مشاركة مصممة وفق قطاعك وقيودك وأهداف نموك.",
  },
  "about.history": { en: "Our history", ar: "تاريخنا" },
  "about.history.p1": {
    en: "Founded by industry veterans, D&A Systems emerged from the need for a partner that understands enterprise architecture, transformation, and governance—helping organizations modernize and navigate complex technology shifts.",
    ar: "تأسست D&A Systems على يد خبراء متمرسين في المجال، استجابةً للحاجة إلى شريك يفهم هندسة المؤسسات والتحول الرقمي والحوكمة—يساعد المنظمات على التحديث والتعامل مع التحولات التكنولوجية المعقدة.",
  },
  "about.team": { en: "Our team", ar: "فريقنا" },
  "about.faq.heading": {
    en: "Frequently asked questions",
    ar: "الأسئلة الشائعة",
  },
  "about.faq.subheading": {
    en: "Quick answers about how we work with enterprises and SMEs.",
    ar: "إجابات سريعة حول كيفية عملنا مع المؤسسات والشركات الصغيرة والمتوسطة.",
  },
  "about.team.p1": {
    en: "Enterprise architects, strategists, governance specialists, and engineers—with multidisciplinary certifications across cloud, security, and data ecosystems.",
    ar: "مهندسو مؤسسات واستراتيجيون ومتخصصون في الحوكمة ومهندسون—بشهادات متعددة التخصصات في بيئات السحابة والأمن والبيانات.",
  },

  // ── Contact page ───────────────────────────────────────
  "contact.badge": {
    en: "We reply within one business day",
    ar: "نرد خلال يوم عمل واحد",
  },
  "contact.title": { en: "Get in touch", ar: "تواصل معنا" },
  "contact.subtitle": {
    en: "Questions about our solutions? Send a message—we'll route it to the right specialist.",
    ar: "أسئلة حول حلولنا؟ أرسل رسالة وسنوجهها إلى المتخصص المناسب.",
  },

  // ── Blog page ──────────────────────────────────────────
  "blog.badge": { en: "Insights", ar: "رؤى" },
  "blog.title": { en: "Latest insights & news", ar: "أحدث الرؤى والأخبار" },
  "blog.subtitle": {
    en: "Perspectives on AI, cybersecurity, and enterprise IT.",
    ar: "رؤى حول الذكاء الاصطناعي والأمن السيبراني وتقنية المعلومات للمؤسسات.",
  },
  "blog.search": { en: "Search", ar: "بحث" },
  "blog.search.placeholder": { en: "Keywords…", ar: "كلمات مفتاحية…" },
  "blog.category": { en: "Category", ar: "التصنيف" },
  "blog.category.placeholder": {
    en: "e.g. Cybersecurity",
    ar: "مثال: الأمن السيبراني",
  },
  "blog.searchBtn": { en: "Search", ar: "بحث" },
  "blog.clear": { en: "Clear", ar: "مسح" },
  "blog.empty": { en: "No posts found.", ar: "لم يتم العثور على مقالات." },
  "blog.viewAll": { en: "View all posts", ar: "عرض جميع المقالات" },
  "blog.readMore": { en: "Read more…", ar: "اقرأ المزيد…" },
  "blog.tags": { en: "Tags:", ar: "الوسوم:" },
  "blog.cta.heading": {
    en: "Stay ahead with expert guidance",
    ar: "ابقَ في المقدمة مع إرشاد الخبراء",
  },
  "blog.cta.subtitle": {
    en: "Partner with D&A Systems for enterprise IT, AI, and cybersecurity.",
    ar: "شارك D&A Systems في تقنية المؤسسات والذكاء الاصطناعي والأمن السيبراني.",
  },
  "blog.cta.button": { en: "Contact us", ar: "تواصل معنا" },

  // ── Services page ──────────────────────────────────────
  "services.badge": { en: "What we deliver", ar: "ما نقدمه" },
  "services.title": { en: "Enterprise Services", ar: "خدمات المؤسسات" },
  "services.subtitle": {
    en: "Comprehensive IT capabilities for secure, intelligent, and governed operations.",
    ar: "قدرات تقنية شاملة لعمليات آمنة وذكية وخاضعة للحوكمة.",
  },
  "services.empty": {
    en: "No services published yet. Add them in the admin dashboard.",
    ar: "لا توجد خدمات منشورة بعد. أضفها من لوحة تحكم المسؤول.",
  },
  "services.cta.heading": {
    en: "Ready to strengthen your operations?",
    ar: "هل أنت مستعد لتعزيز عملياتك؟",
  },
  "services.cta.subtitle": {
    en: "Speak with our team about security, automation, and IT governance tailored to your enterprise.",
    ar: "تحدث مع فريقنا حول الأمن والأتمتة وحوكمة تقنية المعلومات المصممة لمؤسستك.",
  },
  "services.cta.button": { en: "Contact us today", ar: "تواصل معنا اليوم" },

  // ── Gallery page ───────────────────────────────────────
  "gallery.badge": { en: "Our work", ar: "أعمالنا" },
  "gallery.title": { en: "Gallery", ar: "المعرض" },
  "gallery.subtitle": {
    en: "A curated look at projects, events, and moments from across our enterprise engagements.",
    ar: "نظرة مختارة على المشاريع والفعاليات واللحظات من مشاركاتنا المؤسسية.",
  },
  "gallery.empty": {
    en: "No gallery images yet. Check back soon.",
    ar: "لا توجد صور في المعرض بعد. تفضّل بالعودة قريباً.",
  },
  "gallery.close": { en: "Close", ar: "إغلاق" },
  "gallery.viewImage": { en: "View image", ar: "عرض الصورة" },

  // ── Products page ──────────────────────────────────────
  "products.badge": { en: "Solutions", ar: "الحلول" },
  "products.title": {
    en: "Enterprise Products & Applications",
    ar: "منتجات وتطبيقات المؤسسات",
  },
  "products.subtitle": {
    en: "Software built to scale, secure, and accelerate operations across your organization.",
    ar: "برمجيات مصممة للتوسع والأمان وتسريع العمليات عبر مؤسستك.",
  },
  "products.empty": {
    en: "No products yet.",
    ar: "لا توجد منتجات بعد.",
  },
  "products.viewDetails": { en: "View details →", ar: "عرض التفاصيل ←" },
  "products.cta.heading": {
    en: "Explore how our products fit your stack",
    ar: "اكتشف كيف تتكامل منتجاتنا مع بنيتك",
  },
  "products.cta.subtitle": {
    en: "We help enterprises deploy, integrate, and scale software with confidence.",
    ar: "نساعد المؤسسات على نشر البرمجيات ودمجها وتوسيع نطاقها بثقة.",
  },
  "products.cta.button": { en: "Get in touch", ar: "تواصل معنا" },
  "products.detail.cta.heading": {
    en: "Want a demo or deployment plan?",
    ar: "هل تريد عرضاً تجريبياً أو خطة نشر؟",
  },
  "products.detail.cta.subtitle": {
    en: "Our team can walk you through integration, licensing, and rollout.",
    ar: "يمكن لفريقنا إرشادك خلال التكامل والترخيص والنشر.",
  },
  "products.detail.cta.button": { en: "Contact sales", ar: "تواصل مع المبيعات" },

  // ── Not-found page ─────────────────────────────────────
  "notfound.title": { en: "Page not found", ar: "الصفحة غير موجودة" },
  "notfound.subtitle": {
    en: "We couldn't find the page you were looking for.",
    ar: "لم نتمكن من العثور على الصفحة التي تبحث عنها.",
  },
  "notfound.back": { en: "Go back home", ar: "العودة إلى الرئيسية" },

  // ── Home page defaults ─────────────────────────────────
  "home.hero.badge": {
    en: "Enterprise IT · AI · Cybersecurity",
    ar: "تقنية المؤسسات · الذكاء الاصطناعي · الأمن السيبراني",
  },
  "home.hero.title": {
    en: "Next-Generation Enterprise Solutions",
    ar: "حلول المؤسسات من الجيل التالي",
  },
  "home.hero.subtitle": {
    en: "We empower modern enterprises with digital transformation, intelligent automation, and scaleable architectures—secure, innovative, and built for your future.",
    ar: "نمكّن المؤسسات الحديثة بالتحول الرقمي والأتمتة الذكية والبنى التحتية القابلة للتطوير—آمنة ومبتكرة ومصممة لمستقبلك.",
  },
  "home.hero.cta1": { en: "Discover our solutions", ar: "اكتشف حلولنا" },
  "home.hero.cta2": { en: "Get in touch", ar: "تواصل معنا" },
  "home.partners.label": { en: "Trusted by leading organizations", ar: "موثوق به من قبل المنظمات الرائدة" },
  "home.faq.heading": { en: "Frequently asked questions", ar: "الأسئلة الشائعة" },
  "home.cta.heading": {
    en: "Ready to transform your enterprise?",
    ar: "هل أنت مستعد لتحويل مؤسستك؟",
  },
  "home.cta.subtitle": {
    en: "Let's build resilient, intelligent solutions together.",
    ar: "لنبني معاً حلولاً متينة وذكية.",
  },
  "home.cta.button": { en: "Contact us today", ar: "تواصل معنا اليوم" },

  // ── Clients page ───────────────────────────────────────
  "clients.badge": { en: "Trust & partnerships", ar: "الثقة والشراكات" },
  "clients.title": { en: "Clients & Partners", ar: "العملاء والشركاء" },
  "clients.subtitle": {
    en: "Organizations we serve and technology partners that extend our enterprise capabilities.",
    ar: "المؤسسات التي نخدمها وشركاء التقنية الذين يوسّعون قدراتنا المؤسسية.",
  },
  "clients.section.clients.badge": { en: "Our clients", ar: "عملاؤنا" },
  "clients.section.clients.title": { en: "Trusted by industry leaders", ar: "موثوق به من قادة الصناعة" },
  "clients.section.clients.subtitle": {
    en: "Enterprises and growth-stage organizations that rely on D&A Systems for secure, scalable outcomes.",
    ar: "مؤسسات ومنظمات في مرحلة النمو تعتمد على D&A Systems لتحقيق نتائج آمنة وقابلة للتوسع.",
  },
  "clients.section.partners.badge": { en: "Strategic partners", ar: "شركاء استراتيجيون" },
  "clients.section.partners.title": { en: "Technology & delivery partners", ar: "شركاء التقنية والتسليم" },
  "clients.section.partners.subtitle": {
    en: "Alliances that strengthen our cloud, security, and transformation practices.",
    ar: "تحالفات تعزز ممارساتنا في السحابة والأمن والتحول.",
  },
  "clients.cta.heading": {
    en: "Become our next success story",
    ar: "كن قصة نجاحنا التالية",
  },
  "clients.cta.subheading": {
    en: "Let's discuss how D&A Systems can support your enterprise goals.",
    ar: "لنناقش كيف يمكن لـ D&A Systems دعم أهداف مؤسستك.",
  },
  "clients.cta.button": { en: "Start a conversation", ar: "ابدأ المحادثة" },
};

export function t(key: string, lang: Lang): string {
  return dict[key]?.[lang] ?? dict[key]?.en ?? key;
}

export function getDir(lang: Lang): "ltr" | "rtl" {
  return lang === "ar" ? "rtl" : "ltr";
}

export { dict };
